import { Component, Input, EventEmitter, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { FieldsetModule } from 'primeng/fieldset';
import { NotificacoesService } from '@/app/pages/service/admin/notificacoes.service';

@Component({
    standalone: true,
    selector: 'app-responder-dialog',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        RippleModule,
        DialogModule,
        EditorModule,
        InputTextModule,
        FileUploadModule,
        FieldsetModule
    ],
    providers: [
        NotificacoesService
    ],
    templateUrl: './responder-dialog.component.html',
    styleUrls: ['./responder-dialog.component.scss']
})
export class ResponderDialogComponent {
    @ViewChild('fileUpload') fileUpload: any;

    @Input() registro: any = {};
    @Input() registroDialog: boolean = false;
    @Output() dialogFechado: EventEmitter<any> = new EventEmitter();

    registroDocumentos: any[] = [];

    uploadedFiles: any[] = [];
    documentoSelecionado: any = null;
    visualizarDocumentoDialog: boolean = false;

    loading: boolean = false;
    submitted: boolean = false;

    constructor(
        private messageService: MessageService,
        private _notificacaoService: NotificacoesService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['registro'] && this.registroDialog) {
            if (this.registro?.id) {
                this.editarRegistro(this.registro);
                return;
            }

            this.limparCampos();
        }
    }

    editarRegistro(registro: any) {
        this.loading = true;
        this._notificacaoService.listarPorId(registro.id).subscribe({
            next: (data) => {
                this.loading = false;
                this.carregarRegistro(data.result);
            },
            error: (error) => {
                console.error('Erro ao carregar notificação:', error);
                this.loading = false;
                this._message('Não foi possível carregar a notificação.', 'error', 'Erro');
            }
        });
    }

    carregarRegistro(registro: any) {
        if (!registro?.id) return;

        if (this.fileUpload) {
            this.fileUpload.clear();
        }

        this.limparCampos();

        this.registro = { ...registro };

        if (registro.documentos?.length > 0) {
            this.registroDocumentos = registro.documentos.map((doc: any) => ({
                id: doc.id,
                arquivo: doc.arquivo,
                documento: doc.documento
            }));

            this.uploadedFiles = this.registroDocumentos.map((doc: any) => ({
                name: doc.documento,
                size: 1985,
                objectURL: doc.arquivo
            }));
        }
    }

    limparCampos() {
        this.registro = {};
        this.registroDocumentos = [];
        this.uploadedFiles = [];
        this.submitted = false;
    }

    cancelarRegistro(data: any | null) {
        this.limparCampos();
        this.loading = false;
        this.registroDialog = false;
        this.dialogFechado.emit(data);
    }

    salvarRegistro() {
        this.submitted = true;

        if (!this.registro?.respostaNotificacao?.trim()) {
            this._message('Por favor, escreva uma mensagem antes de responder.', 'info', 'Ops');
            return;
        }

        const payload = this.montarPayload();

        this.loading = true;
        this._notificacaoService.responder(payload.notificacaoId, payload).subscribe({
            next: (data) => {
                this.loading = false;

                if (!data?.success) {
                    this._message('Não foi possível responder a notificação.', 'error', 'Erro');
                    return;
                }

                this._success(data);
                this.cancelarRegistro(data);
            },
            error: () => {
                this.loading = false;
                this._message('Ocorreu um erro ao tentar responder a notificação.', 'error', 'Erro');
            }
        });
    }

    montarPayload(): any {
        return {
            notificacaoId: this.registro.id,
            usuarioRemetenteId: this.registro.usuarioRemetente?.id,
            notificacao: this.registro.respostaNotificacao,
            documentos: this.registroDocumentos.map(documento => ({ ...documento }))
        };
    }

    onFileSelect(event: any) {
        const files = event.files;

        if (this.registroDocumentos.length >= 3) {
            this._message('Você só pode adicionar no máximo 3 anexos por notificação.', 'error', 'Limite de anexos excedido');
            return;
        }

        if (this.registroDocumentos.length + files.length > 3) {
            this._message(`Você só pode adicionar mais ${3 - this.registroDocumentos.length} arquivo(s).`, 'error', 'Limite de anexos excedido');
            return;
        }

        for (let file of files) {
            if (this.registroDocumentos.some(documento => documento.documento === file.name)) {
                this._message(`O arquivo "${file.name}" já foi adicionado.`, 'warn', 'Arquivo duplicado');
                continue;
            }

            const reader = new FileReader();

            reader.onload = (e: any) => {
                this.registroDocumentos.push({
                    id: null,
                    documento: file.name,
                    arquivo: e.target.result
                });
                this.uploadedFiles.push(file);
            };

            reader.readAsDataURL(file);
        }
    }

    onRemove(event: any) {
        const file = event.file;
        this.registroDocumentos = this.registroDocumentos.filter(documento => documento.documento !== file.name);
        this.uploadedFiles = this.uploadedFiles.filter(f => f.name !== file.name);

        this._message(`${file.name} foi removido.`, 'info', 'Arquivo removido');
    }

    onClear() {
        this.registroDocumentos = [];
        this.uploadedFiles = [];
    }

    isImage(fileName: string): boolean {
        const extensoesImagem = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        return extensoesImagem.some(ext => fileName.toLowerCase().endsWith(ext));
    }

    visualizarDocumento(documento: any) {
        this.documentoSelecionado = documento;
        this.visualizarDocumentoDialog = true;
    }

    _success(data: any) {
        this.loading = false;
        data.messages.forEach((msg: any) => {
            if (msg.applicationMessageType === 0) {
                this._message(msg.message, "success", "Sucesso");
            }

            if (msg.applicationMessageType === 1) {
                this._message(msg.message, "error", "Erro");
            }

            if (msg.applicationMessageType === 2) {
                this._message(msg.message, "warn", "Alerta");
            }
        })
    }

    _message(message: string, severity: string, summary: string) {
        this.messageService.add({
            severity: severity,
            summary: summary,
            detail: message,
            life: 4000
        });
    }
}
